/**
 * The bulk action bar.
 *
 * IT APPEARS ONLY WHEN SOMETHING IS SELECTED, and the first thing it offers is
 * «انتخاب همهٔ N مورد منطبق» — because the difference between "these 25 rows"
 * and "all 4,812 matching" is the single most consequential ambiguity on this
 * screen, and a checkbox in a header cannot express it.
 *
 * When "all matching" is chosen the bar sends the FILTER downstream, never a
 * list of ids. The client never held those ids.
 */
import { Button } from '@meshhub/design-tokens/ui';
import { useMutation } from '@apollo/client';
import { Check, MessageSquare, Tag, TrendingUp, X } from 'lucide-react';
import React, { useState } from 'react';
import { SendMessageDialog } from '../messaging/SendMessageDialog.web.jsx';
import { COLLABORATION_STATUS_LABELS, formatCount } from '../../lib/format.js';
import { BULK_CHANGE_STATUS, BULK_TAG, BULK_UNTAG } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

const PIPELINE = ['UNKNOWN', 'INTERESTED', 'CONSULTING', 'SUBSCRIBER'];

export function BulkBar({
  selector,
  selectedCount,
  totalCount,
  allMatching,
  bulkLimit,
  isAdmin,
  tags,
  /**
   * A contact from the current page, to preview the message AGAINST. The
   * rendered body is per person, so the dialog shows one real example rather
   * than a rendering that belongs to nobody. Undefined is fine — the dialog
   * then simply has no preview.
   */
  sampleContact,
  onSelectAllMatching,
  onClear,
  onDone,
}) {
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // 'tag' | 'untag' | 'status'
  const [statusReason, setStatusReason] = useState('');
  const [sending, setSending] = useState(false);

  const [bulkTag, tagState] = useMutation(BULK_TAG);
  const [bulkUntag, untagState] = useMutation(BULK_UNTAG);
  const [bulkStatus, statusState] = useMutation(BULK_CHANGE_STATUS);
  const busy = tagState.loading || untagState.loading || statusState.loading;

  const run = async (fn) => {
    setError(null);
    setMessage(null);
    try {
      const result = await fn();
      const payload = Object.values(result.data ?? {})[0];
      setMessage(S.search.bulkDone.replace('{n}', formatCount(payload?.affected ?? 0)));
      setMode(null);
      onDone();
    } catch (err) {
      // The server's own words. It refuses above the cap with the real number
      // in the message, and paraphrasing that would lose it.
      setError(err.message);
    }
  };

  const overLimit = allMatching && totalCount > bulkLimit;

  return (
    <div
      className="flex shrink-0 flex-col gap-2 border-b border-border bg-surface-hover px-6 py-2"
      role="region"
      aria-label={S.search.selectedCount.replace('{n}', formatCount(selectedCount))}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium">
          {S.search.selectedCount.replace('{n}', formatCount(selectedCount))}
        </span>

        {!allMatching && totalCount > selectedCount && (
          <Button variant="ghost" onClick={onSelectAllMatching}>
            {S.search.selectAllMatching.replace('{n}', formatCount(totalCount))}
          </Button>
        )}

        <Button onClick={() => setMode(mode === 'tag' ? null : 'tag')} disabled={busy}>
          <Tag size={13} aria-hidden="true" />
          {S.search.bulkTag}
        </Button>
        <Button onClick={() => setMode(mode === 'untag' ? null : 'untag')} disabled={busy}>
          {S.search.bulkUntag}
        </Button>
        {isAdmin && (
          <Button onClick={() => setMode(mode === 'status' ? null : 'status')} disabled={busy}>
            <TrendingUp size={13} aria-hidden="true" />
            {S.search.bulkStatus}
          </Button>
        )}
        {/* ADMIN, like the status change beside it and for a sharper version
            of the same reason: this is the one action on this bar that leaves
            the building. «Everything that sends to many people is ADMIN»
            (CLAUDE.md §8e) — the blast radius of a mistake is the difference
            between one awkward message and a banned business number. Absent
            for a USER rather than present-and-refused, like every other ADMIN
            affordance in this client. */}
        {isAdmin && (
          <Button onClick={() => setSending(true)} disabled={busy || overLimit}>
            <MessageSquare size={13} aria-hidden="true" />
            {S.messaging.bulkSend}
          </Button>
        )}

        <Button variant="ghost" className="ms-auto" onClick={onClear}>
          <X size={13} aria-hidden="true" />
          {S.search.clearSelection}
        </Button>
      </div>

      {overLimit && (
        <p className="text-sm text-warning">
          {S.search.bulkTooLarge} ({formatCount(totalCount)} / {formatCount(bulkLimit)})
        </p>
      )}

      {mode === 'tag' || mode === 'untag' ? (
        <div className="flex flex-wrap items-center gap-2">
          {tags.map((tag) => (
            <Button
              key={tag.id}
              disabled={busy}
              onClick={() =>
                run(() =>
                  (mode === 'tag' ? bulkTag : bulkUntag)({
                    variables: { selector, tagId: tag.id },
                  }),
                )
              }
            >
              {tag.name}
            </Button>
          ))}
        </div>
      ) : null}

      {mode === 'status' && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={statusReason}
            onChange={(event) => setStatusReason(event.target.value)}
            placeholder={S.status.reasonOptional}
            className="h-control w-72 rounded border border-border bg-bg px-3 text-sm
                       outline-none focus:border-primary"
          />
          {PIPELINE.map((status) => (
            <Button
              key={status}
              disabled={busy}
              onClick={() =>
                run(() =>
                  bulkStatus({
                    variables: { selector, toStatus: status, reason: statusReason || null },
                  }),
                )
              }
            >
              {COLLABORATION_STATUS_LABELS[status]}
            </Button>
          ))}
          <span className="text-xs text-text-muted">
            {S.status.reasonRequiredBackward}
          </span>
        </div>
      )}

      {/* A bulk send creates a CAMPAIGN, which is a durable object with a
          frozen recipient list and a cancel path. Said on the bar, because an
          «ارسال» button that quietly creates one would be a surprise. */}
      {isAdmin && sending && (
        <p className="text-xs text-text-muted">{S.messaging.bulkSendHint}</p>
      )}

      <SendMessageDialog
        open={sending}
        onOpenChange={setSending}
        selector={selector}
        recipientCount={selectedCount}
        sampleContact={sampleContact}
        onSent={() => {
          setMessage(S.messaging.bulkSendQueued);
          onDone();
        }}
      />

      {message && (
        <p className="flex items-center gap-1 text-sm text-success">
          <Check size={13} aria-hidden="true" />
          {message}
        </p>
      )}
      {error && <p className="text-sm text-danger">{error}</p>}
    </div>
  );
}
